import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MenuItem, TextField } from "@mui/material";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { usePermissoes } from "../../contexts/PermissoesContext.tsx";
import empresaService from "../../services/empresaService.ts";
import { Link } from "react-router-dom";
import { useFeedback } from "../../contexts/FeedbackContext.tsx";
import relatorioService, { LinhaLogAtividade } from "../../services/relatorioService.ts";
import Button from "../../ui/Button.tsx";
import SkeletonRows from "../../ui/SkeletonRows.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import "../../styles/design-system.css";

const TAMANHO_PAGINA = 25;

// Mesma receita de cores dos demais modulos: sem ThemeProvider do MUI, os
// campos caem no tema claro padrao e ficam ilegiveis no dark mode.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
  },
};

const formatarDataHora = (iso: string) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

const LogAtividades: React.FC = () => {
  const { catalogo } = usePermissoes();
  const { showFeedback } = useFeedback();

  const [linhas, setLinhas] = useState<LinhaLogAtividade[]>([]);
  const [pagina, setPagina] = useState(0);
  const [totalPaginas, setTotalPaginas] = useState(0);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [exportando, setExportando] = useState(false);

  // null enquanto nao se sabe: assumir "desligado" antes da resposta faria a
  // tela piscar o aviso de desligado para quem tem o registro ligado.
  const [logAtivo, setLogAtivo] = useState<boolean | null>(null);
  const [souAdm, setSouAdm] = useState(false);

  const [acao, setAcao] = useState("");
  const [dataInicial, setDataInicial] = useState("");
  const [dataFinal, setDataFinal] = useState("");

  /**
   * As acoes do filtro saem do mesmo catalogo que o servidor usa para
   * autorizar — nada escrito a mao aqui. LOGIN_FALHO nao e uma funcionalidade
   * (ninguem "tem permissao de errar a senha"), entao entra a parte.
   */
  const opcoesDeAcao = useMemo(() => {
    const doCatalogo = (catalogo?.grupos ?? []).flatMap((g) =>
      g.funcionalidades.map((f) => ({ codigo: f.codigo, rotulo: `${g.grupo} — ${f.rotulo}` }))
    );
    return [
      { codigo: "LOGIN_FALHO", rotulo: "Acesso — Tentativa de login sem sucesso" },
      ...doCatalogo,
    ];
  }, [catalogo]);

  const filtros = useMemo(
    () => ({
      acao: acao || undefined,
      dataInicial: dataInicial || undefined,
      dataFinal: dataFinal || undefined,
    }),
    [acao, dataInicial, dataFinal]
  );

  const buscar = useCallback(async () => {
    try {
      setIsLoading(true);
      const p = await relatorioService.listar({ ...filtros, page: pagina, size: TAMANHO_PAGINA });
      setLinhas(p?.content ?? []);
      setTotalPaginas(p?.totalPages ?? 0);
      setTotal(p?.totalElements ?? 0);
    } catch (err: any) {
      showFeedback(err.message || "Erro ao carregar o log", "error");
      setLinhas([]);
    } finally {
      setIsLoading(false);
    }
  }, [filtros, pagina, showFeedback]);

  useEffect(() => {
    buscar();
  }, [buscar]);

  useEffect(() => {
    empresaService
      .getAtual()
      .then((resp: any) => {
        const dto = resp?.data ?? resp;
        setLogAtivo(Boolean(dto?.logAtividadeAtivo));
        setSouAdm(Boolean(dto?.podeAlterarConfiguracoes));
      })
      // Falha aqui só esconde o aviso; a tabela em si não depende disso.
      .catch(() => setLogAtivo(null));
  }, []);

  // Trocar filtro volta para a primeira pagina: continuar na pagina 7 de um
  // resultado que agora tem 2 mostraria uma tela vazia sem explicacao.
  useEffect(() => {
    setPagina(0);
  }, [acao, dataInicial, dataFinal]);

  const exportarPdf = async () => {
    try {
      setExportando(true);
      const { linhas: todas, truncado } = await relatorioService.listarTudoParaExportar(filtros);
      if (todas.length === 0) {
        showFeedback("Nenhum registro para exportar com esse filtro.", "info");
        return;
      }

      // autoTable sobre os dados, nunca html2canvas: o export do Dashboard ja
      // travou o navegador desse jeito uma vez.
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(14);
      doc.text("Log de atividades", 14, 16);
      doc.setFontSize(9);
      const periodo = [
        dataInicial ? `de ${dataInicial}` : null,
        dataFinal ? `até ${dataFinal}` : null,
      ].filter(Boolean).join(" ");
      doc.text(
        `Gerado em ${new Date().toLocaleString("pt-BR")}${periodo ? ` — ${periodo}` : ""}`,
        14, 22
      );

      autoTable(doc, {
        startY: 27,
        styles: { fontSize: 8 },
        head: [["Data e hora", "Usuário", "Ação", "Área", "Resultado", "IP"]],
        body: todas.map((l) => [
          formatarDataHora(l.dataHora),
          l.usuarioEmail || "—",
          l.acaoRotulo,
          l.grupo,
          l.sucesso ? "OK" : "Negada",
          l.ip || "—",
        ]),
      });

      doc.save(`log-atividades-${new Date().toISOString().slice(0, 10)}.pdf`);
      if (truncado) {
        showFeedback(
          "O PDF traz os 2000 registros mais recentes do filtro. Estreite o período para incluir menos.",
          "info"
        );
      }
    } catch (err: any) {
      showFeedback(err.message || "Erro ao gerar o PDF", "error");
    } finally {
      setExportando(false);
    }
  };

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Log de atividades</h1>
          <p>
            Quem fez o quê e quando. Registra ações que alteram dados — consultas não entram.
          </p>
        </div>
        <div className="ds-page-head-actions">
          <Button
            variant="outline"
            icon={<PictureAsPdfOutlinedIcon fontSize="small" />}
            onClick={exportarPdf}
            disabled={exportando || isLoading}
          >
            {exportando ? "Gerando..." : "Exportar PDF"}
          </Button>
        </div>
      </div>

      {/*
        Sem este aviso, a tela vazia de uma empresa com o registro desligado
        seria indistinguível de "ninguém fez nada" — e ninguém descobriria que
        existe uma chave para ligar.
      */}
      {logAtivo === false && (
        <div
          className="ds-card"
          style={{
            padding: 16,
            marginBottom: 16,
            border: "1px solid var(--orange)",
            background: "var(--orange-tint)",
            fontSize: 12.8,
            lineHeight: 1.6,
          }}
        >
          <strong>O registro de atividades está desligado para esta empresa.</strong> Nada novo está
          sendo gravado — o que aparece abaixo, se houver, é histórico de quando esteve ligado.{" "}
          {souAdm ? (
            <>
              Para ligar, vá em{" "}
              <Link to="/configuracoes?aba=empresa" style={{ fontWeight: 700, color: "var(--blue)" }}>
                Configurações → Empresa
              </Link>
              .
            </>
          ) : (
            <>Fale com o responsável pela conta para ligar.</>
          )}
        </div>
      )}

      <div className="ds-toolbar" style={{ gap: 12, flexWrap: "wrap" }}>
        <TextField
          select label="Ação" size="small" sx={{ ...fieldSx, minWidth: 260 }}
          value={acao} onChange={(e) => setAcao(e.target.value)}
          SelectProps={{
            MenuProps: {
              sx: { "& .MuiPaper-root": { bgcolor: "var(--surface)", color: "var(--ink)" } },
            },
          }}
        >
          <MenuItem value="">Todas as ações</MenuItem>
          {opcoesDeAcao.map((o) => (
            <MenuItem key={o.codigo} value={o.codigo}>{o.rotulo}</MenuItem>
          ))}
        </TextField>

        {/* input nativo de propósito — @mui/x-date-pickers está quebrado neste projeto */}
        <TextField
          label="De" type="date" size="small" sx={fieldSx}
          InputLabelProps={{ shrink: true }}
          value={dataInicial} onChange={(e) => setDataInicial(e.target.value)}
        />
        <TextField
          label="Até" type="date" size="small" sx={fieldSx}
          InputLabelProps={{ shrink: true }}
          value={dataFinal} onChange={(e) => setDataFinal(e.target.value)}
        />

        {!isLoading && (
          <span style={{ fontSize: 12.6, color: "var(--ink-faint)", marginLeft: "auto" }}>
            {total} {total === 1 ? "registro" : "registros"}
          </span>
        )}
      </div>

      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th>Data e hora</th>
              <th>Usuário</th>
              <th>Ação</th>
              <th>Área</th>
              <th>Resultado</th>
              <th>IP</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows columns={6} />
            ) : linhas.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState title="Nenhuma atividade encontrada" />
                </td>
              </tr>
            ) : (
              linhas.map((l) => (
                <tr key={l.id}>
                  <td className="ds-cell-title">{formatarDataHora(l.dataHora)}</td>
                  <td>{l.usuarioEmail || "—"}</td>
                  <td>{l.acaoRotulo}</td>
                  <td>{l.grupo}</td>
                  <td>
                    <span className={`ds-badge ${l.sucesso ? "ds-badge-green" : "ds-badge-red"}`}>
                      {l.sucesso ? "OK" : "Negada"}
                    </span>
                  </td>
                  <td>{l.ip || "—"}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* mesmo formato da listagem de cirurgias: .ds-page-btn tem 30x30 fixo,
          texto nao cabe dentro dele */}
      {totalPaginas > 1 && (
        <div className="ds-pagination" style={{ justifyContent: "center", gap: 16 }}>
          <button
            className="ds-page-btn"
            onClick={() => setPagina((p) => Math.max(0, p - 1))}
            disabled={pagina === 0 || isLoading}
            aria-label="Página anterior"
          >
            <ChevronLeftIcon fontSize="small" />
          </button>
          <span>Página {pagina + 1} de {totalPaginas}</span>
          <button
            className="ds-page-btn"
            onClick={() => setPagina((p) => Math.min(totalPaginas - 1, p + 1))}
            disabled={pagina + 1 >= totalPaginas || isLoading}
            aria-label="Próxima página"
          >
            <ChevronRightIcon fontSize="small" />
          </button>
        </div>
      )}
    </div>
  );
};

export default LogAtividades;
